<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100 text-center hidden lg:block">
      <md-button class="md-primary" @click="exportDefaultTranscript()">
        <i class="fas fa-file-download mr-1"></i>
        DOWNLOAD PDF
      </md-button>
    </div>

    <div class="md-layout-item md-size-100 transcript-mobile">
      <div v-if="dataTranscript" class="transcript-view w-[800px]">
        <div class="content-transcript format-letter bg-unofficial">
          <div class="flex mb-2 leading-[18px]">
            <div class="w-1/2">
              <h1 class="text-[23px] text-[#2d82b5] font-bold">
                {{ dataTranscript.school.name }}
              </h1>
              <div class="mt-2 text-[11px]">
                <p>
                  School code:
                  {{ dataTranscript.school.school_code }}
                </p>
                <p>
                  Phone:
                  {{ dataTranscript.school.phone }}
                </p>
                <p>
                  Website:
                  {{ dataTranscript.school.website }}
                </p>
                <p>
                  Address:
                  {{ dataTranscript.school.address }}
                </p>
              </div>
            </div>
            <div class="w-1/2 text-[11px]">
              <div class="flex">
                <div class="w-1/2">
                  <p class="font-bold">
                    Name:
                    {{ dataTranscript.student.full_name }}
                  </p>
                  <p>
                    Graduation date:
                    {{
                      convertDateFromServer(
                        dataTranscript.student.graduation,
                        "date"
                      )
                    }}
                  </p>
                  <p>
                    Grade:
                    {{ dataTranscript.student.grade }}
                  </p>
                </div>
                <div class="w-1/2 pl-2">
                  <p class="font-bold">
                    Student ID:
                    {{ dataTranscript.student.student_code }}
                  </p>
                  <p>
                    Enrollment date:
                    {{
                      convertDateFromServer(
                        dataTranscript.student.entered,
                        "date"
                      )
                    }}
                  </p>
                  <p>
                    Date of birth:
                    {{
                      convertDateFromServer(
                        dataTranscript.student.birthday,
                        "date"
                      )
                    }}
                  </p>
                </div>
              </div>
              <div>
                <p class="address">
                  Address:
                  {{ dataTranscript.studentAddress }}
                </p>
                <p>
                  Retrieval date:
                  {{ retrievalDate }}
                </p>
              </div>
            </div>
          </div>
          <div class="md-layout h-[900px] overflow-hidden leading-[16px]">
            <div class="md-layout-item md-size-33 px-0">
              <div class="flex flex-col">
                <!-- ACADEMIC SUMMARY-->
                <div class="mt-3">
                  <p
                    class="text-center font-semibold col-header py-1 text-[11px]"
                  >
                    Academic summary
                  </p>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Cumulative GPA unweighted:
                    </span>
                    {{ dataTranscript.academicSummary.unWeighted }}
                  </div>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Class rank:
                    </span>
                    {{ dataTranscript.academicSummary.rankUnWeighted }}
                  </div>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Cumulative GPA weighted:
                    </span>
                    {{ dataTranscript.academicSummary.weighted }}
                  </div>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Class rank:
                    </span>
                    {{ dataTranscript.academicSummary.rankWeighted }}
                  </div>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Cumulative GPA with bonus points:
                    </span>
                    {{ dataTranscript.academicSummary.bonus }}
                  </div>
                  <div class="text-[9px] px-2">
                    <span class="font-semibold">
                      Class rank:
                    </span>
                    {{ dataTranscript.academicSummary.rankBonus }}
                  </div>
                </div>

                <!-- ATTENDANCE-->
                <div class="mt-3">
                  <p
                    class="text-center font-semibold col-header py-1 text-[11px]"
                  >
                    Attendance
                  </p>
                  <div
                    class="block-score block-score-attendance font-semibold mt-1"
                  >
                    <div class="term">Term</div>
                    <div class="school-days">School days</div>
                    <div class="member-days">Member days</div>
                    <div class="absent-days">Absent days</div>
                    <div class="tardy">Tardy</div>
                  </div>
                  <div
                    v-for="(attendance, idx) in dataTranscript.attendancesData"
                    :key="'attendance' + idx"
                    class="block-score block-score-attendance"
                  >
                    <div class="term">{{ attendance.termName }}</div>
                    <div class="school-days">{{ attendance.schoolDay }}</div>
                    <div class="member-days">{{ attendance.memberDay }}</div>
                    <div class="absent-days">{{ attendance.absenceDay }}</div>
                    <div class="tardy">{{ attendance.tardyDay }}</div>
                  </div>
                  <div
                    class="block-score block-score-attendance font-bold text-[#2d82b5]"
                  >
                    <div class="term">
                      Total:
                      {{ dataTranscript.totalAttendanceData.totalTerm }}
                    </div>
                    <div class="school-days">
                      {{ dataTranscript.totalAttendanceData.totalSchoolDay }}
                    </div>
                    <div class="member-days">
                      {{ dataTranscript.totalAttendanceData.totalMemberDay }}
                    </div>
                    <div class="absent-days">
                      {{ dataTranscript.totalAttendanceData.totalAbsenceDay }}
                    </div>
                    <div class="tardy-days">
                      {{ dataTranscript.totalAttendanceData.totalTardyDay }}
                    </div>
                  </div>
                </div>

                <!-- CREDIT SUMMARY -->
                <div class="mt-3">
                  <div class="col-header py-1">
                    <div class="text-[10px] font-bold px-1">
                      <p>Credit summary</p>
                      <p>
                        Curriculum program:
                        {{ dataTranscript.programName }}
                      </p>
                    </div>
                    <div class="block-score block-score-credit">
                      <div class="name">Grad.category</div>
                      <div class="earned">Earned</div>
                      <div class="required">Required</div>
                      <div class="remaining">Remaining</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div
                    v-for="(item, index) in dataTranscript.creditSummary
                      .graduationCategories"
                    :key="'credit summary' + index"
                  >
                    <div class="block-score block-score-credit">
                      <div class="name">{{ item.name }}</div>
                      <div class="earned">{{ item.credit }}</div>
                      <div class="required">
                        {{ item.graduation_category_credit }}
                      </div>
                      <div class="remaining">
                        {{ item.remaining | formatScoreNegative }}
                      </div>
                    </div>
                  </div>
                  <div
                    class="block-score block-score-credit font-bold text-[#2d82b5]"
                  >
                    <div class="name">Total</div>
                    <div class="earned">
                      {{ dataTranscript.creditSummary.total.earned }}
                    </div>
                    <div class="required">
                      {{ dataTranscript.creditSummary.total.required }}
                    </div>
                    <div class="remaining">
                      {{
                        dataTranscript.creditSummary.total.remaining
                          | formatScoreNegative
                      }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="md-layout-item md-size-66 pl-3 pr-0 h-full">
              <div
                class="flex flex-col flex-wrap content-start gap-x-[10px] h-full overflow-hidden"
              >
                <!-- TRANSFER SCHOOL-->
                <div
                  v-for="(school, index) in dataTranscript.transferSchool"
                  :key="'transfer school' + index"
                  class="content-item"
                >
                  <div class="mt-3">
                    <div class="col-header py-1">
                      <div class="text-center font-semibold text-[11px] px-1">
                        {{ school[0].informationSchool }}
                      </div>
                      <div class="block-score block-score-term">
                        <div class="course">Course</div>
                        <div class="mark text-center">Mark</div>
                        <div class="weight text-right">Weight</div>
                        <div class="credit text-right">Credit</div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-for="(item, index) in school"
                    :key="'transfer school data' + index"
                  >
                    <div class="font-bold text-[9px] p-1">
                      {{ item.semester_name }}
                    </div>
                    <div
                      v-for="(subject, idx) in item.subjects"
                      :key="'transfer school subject' + idx"
                      class="block-score block-score-term"
                    >
                      <div class="course">
                        {{ renderCourseId(subject) }} {{ subject.subject_name }}
                      </div>
                      <div class="mark text-center">
                        {{ subject.grade_letter }}
                      </div>
                      <div class="weight text-right">{{ subject.weight }}</div>
                      <div class="credit text-right">{{ subject.credit }}</div>
                    </div>
                    <div
                      style="color: #2d82b5; line-height: 25px; font-weight: bold; font-size: 9px; text-align: center;"
                    >
                      Earned credits:
                      {{ Number(item.earned.earned_credit).toFixed(2) || "" }}
                      <span class="ml-5"
                        >GPA: {{ item.earned.earned_gpa || "" }}</span
                      >
                    </div>
                  </div>
                </div>

                <!-- CURRENT SCHOOL-->
                <div
                  v-if="dataTranscript.currentSchool.length"
                  class="content-item mt-3"
                >
                  <div class="col-header py-1">
                    <div class="text-center font-semibold text-[11px] px-1">
                      {{ dataTranscript.school.name }}
                    </div>
                    <div class="block-score block-score-term">
                      <div class="course">Course</div>
                      <div class="mark text-center">Mark</div>
                      <div class="weight text-right">Weight</div>
                      <div class="credit text-right">Credit</div>
                    </div>
                  </div>
                </div>
                <div
                  v-for="(item, index) in dataTranscript.currentSchool"
                  :key="'current school' + index"
                  class="content-item"
                >
                  <div class="font-bold text-[9px] p-1">
                    {{ item.semester_name }}
                  </div>
                  <div
                    v-for="(subject, idx) in item.subjects"
                    :key="'current school subject' + idx"
                    class="block-score block-score-term"
                  >
                    <div class="course">
                      {{ renderCourseId(subject) }} {{ subject.subject_name }}
                    </div>
                    <div class="mark text-center">
                      {{ subject.grade_letter }}
                    </div>
                    <div class="weight text-right">{{ subject.weight }}</div>
                    <div class="credit text-right">{{ subject.credit }}</div>
                  </div>
                  <div
                    style="color: #2d82b5; line-height: 25px; font-weight: bold; font-size: 9px; text-align: center;"
                  >
                    Earned credits:
                    {{ Number(item.earned.earned_credit).toFixed(2) || "" }}
                    <span class="ml-5"
                      >GPA: {{ item.earned.earned_gpa || "" }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <pdf-default
      v-if="dataTranscript"
      ref="defaultTranscriptPdf"
      :enableDownload="true"
      :filename="fileName"
      :preview-modal="false"
      pdf-content-width="800px"
      pdf-format="letter"
    >
      <template #pdf-content>
        <DefaultTranscriptPdf
          :data-transcript="dataTranscript"
        ></DefaultTranscriptPdf>
      </template>
    </pdf-default>
  </div>
</template>
<script>
import { convertDateFromServer } from "@/helpers/datetime-helper";
import moment from "moment";
import { service_paths } from "@/constants/Services";
import PdfDefault from "../../../components/PdfDefault";
import DefaultTranscriptPdf from "./DefaultTranscriptPdf";

export default {
  name: "default-transcript",
  components: { DefaultTranscriptPdf, PdfDefault },
  props: ["programId"],
  data() {
    return {
      convertDateFromServer,
      retrievalDate: moment().format("YYYY-MM-DD hh:mm A"),
      dataTranscript: null
    };
  },
  filters: {
    formatScoreNegative(value) {
      return Number(value) < 0 ? 0 : Number(value).toFixed(2);
    }
  },
  computed: {
    fileName() {
      return `${this.dataTranscript?.student?.full_name}_${this.retrievalDate}`;
    }
  },
  watch: {
    programId(newId) {
      this.getTranscript(newId);
    }
  },
  methods: {
    exportDefaultTranscript() {
      this.$refs.defaultTranscriptPdf.generatePdf(false);
    },

    getTranscript(programId) {
      const param = {
        user_id: this.$route.params.id,
        program_id: programId
      };
      this.$axios
        .get(`${service_paths.export}/transcript-default`, param)
        .then(response => {
          this.dataTranscript = this.generateDataTranscript(response.data);
        });
    },

    generateDataTranscript(response) {
      return {
        school: response.school,
        student: response.student,
        parent: response.parent,
        studentAddress: this.getStudentAddress(response.student),
        academicSummary: this.generateAcademicSummary(response),
        programName: response.programName,
        attendancesData: response.attendancesTranscript?.attendances,
        totalAttendanceData: response.attendancesTranscript?.totalAttendance,
        transferSchool: Object.values(response.scores?.transferSchool),
        currentSchool: response.scores?.currentSchool,
        creditSummary: {
          graduationCategories: response.graduationCategories,
          total: this.totalCreditSummary(response.graduationCategories)
        }
      };
    },

    getStudentAddress(address) {
      return [
        address.primary_street,
        address.primary_city,
        address.primary_state,
        address.primary_zip
      ]
        .filter(item => item)
        .join(", ");
    },

    generateAcademicSummary(response) {
      return {
        unWeighted: response.gpaUnweighted.gpaUnweighted,
        rankUnWeighted: response.gpaUnweighted.rankUnweighted,
        weighted: response.cpa,
        rankWeighted: response.gpaUnweighted.rankWeighted,
        bonus: response.cpaBonus,
        rankBonus: response.gpaUnweighted.rankBonus
      };
    },

    totalCreditSummary(graduation) {
      let totalEarned = 0,
        totalRequired = 0,
        totalRemaining = 0;
      graduation.map(item => {
        totalEarned += Number(item.credit);
        totalRequired += Number(item.graduation_category_credit);
        totalRemaining =
          Number(item.remaining) > 0
            ? totalRemaining + Number(item.remaining)
            : totalRemaining;
      });
      return {
        earned: totalEarned,
        required: totalRequired,
        remaining: totalRemaining
      };
    },

    renderGradeName(gradeName) {
      switch (gradeName) {
        case "PS":
        case "PK":
        case "K":
          return "0";
        case "10":
        case "11":
        case "12":
          return "9";
        default:
          return gradeName || "";
      }
    },

    renderSubjectType(subjectType) {
      switch (subjectType) {
        case "honors":
          return "4";
        case "advanced placement":
          return "2";
        case "normal":
          return "0";
        default:
          return "";
      }
    },

    renderCourseId(subject) {
      const subjectCode = subject.subject_code || "";
      const subjectType = this.renderSubjectType(subject.subject_type);
      const gradeName = this.renderGradeName(subject.grade_name);
      const termCourseCode = subject.term_course_code || "";
      return `${subjectCode}${subjectType}${gradeName}${termCourseCode}`;
    }
  },
  provide() {
    return {
      renderCourseId: this.renderCourseId
    };
  },
  created() {
    this.getTranscript(this.programId);
  }
};
</script>
<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Cairo&display=swap");

.transcript-view {
  margin: 10px auto;
  padding: 10px 20px 20px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 10px 5px #dcdcdc;
}

@media (max-width: 599px) {
  .transcript-mobile {
    overflow-x: scroll;
  }

  .transcript-view {
    margin: 10px;
  }
}

.content-transcript {
  width: 100%;
  font-family: "Cairo", sans-serif;
  color: #000;
  padding-top: 0.25rem;

  table,
  td {
    border: 1px solid #93969a !important;
    border-collapse: collapse;
  }
}

.font-cairo {
  font-family: "Cairo", sans-serif;
}

.bg-unofficial {
  background-image: url("/img/bg-unofficial.png");
  background-repeat: no-repeat;
  background-position: center;
}

.format-letter {
  height: 1015px;
  max-height: 1015px;
}

.format-a4 {
  height: 1083px;
  max-height: 1083px;
}

p.address {
  word-break: break-word;
}

.content-item {
  width: 48.5%;
  border-bottom: 1px solid rgba(45, 130, 181, 0.25);
}

.col-header {
  background-color: #2d82b5;
  color: white;
  border-radius: 3px !important;
}

.block-score {
  display: flex;
  font-size: 9px;
  padding: 0 4px;
}

.block-score-attendance {
  .term {
    width: 40%;
  }

  .school-days,
  .member-days,
  .absent-days,
  .tardy-days {
    width: 15%;
  }
}

.block-score-credit {
  .name {
    width: 40%;
  }

  .earned,
  .remaining,
  .required {
    width: 20%;
  }
}

.block-score-term {
  .course {
    width: 55%;
  }

  .mark,
  .credit,
  .weight {
    width: 15%;
  }
}
</style>
